import { useEffect, useMemo } from 'react'
import { FilterDrawerType } from '@/components/CourseFiltersDrawer/types'
import { usePublicCollectionFilters } from '@/hooks/usePublicCollectionFilters/usePublicCollectionFilters'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { createFiltersSelector, setFilters } from '@/store/slices/aggregatesSlice'
import { useCollectionFilters } from './useCollectionFilters/useCollectionFilters'

type Props = {
  type: FilterDrawerType
}

export const useCourseFilter = ({ type }: Props) => {
  const dispatch = useAppDispatch()

  const filters = useAppSelector((state) => createFiltersSelector(state, type))
  const filterCount = useMemo(() => {
    const { category, ...otherFilters } = filters

    const regularFilterCount = Object.values(otherFilters).reduce((sum, filter) => sum + filter.length, 0)

    const categoryFilterCount = category
      ? Object.values(category).reduce((sum, attributes) => sum + attributes.length, 0)
      : 0

    return regularFilterCount + categoryFilterCount
  }, [filters])

  const getCollectionFilters = type === 'public' ? usePublicCollectionFilters : useCollectionFilters
  const { collectionFilters } = getCollectionFilters(type)

  useEffect(() => {
    if (!collectionFilters.length) return

    const availableCollections = filters.collection.filter(
      (collectionId) => collectionFilters.findIndex((collection) => collection.id === collectionId) >= 0,
    )

    // Exclude unavailable collections from filters.
    if (availableCollections.length !== filters.collection.length) {
      dispatch(setFilters({ type, filters: { ...filters, collection: availableCollections } }))
    }
  }, [dispatch, type, filters, collectionFilters])

  return {
    collectionFilters,
    filterCount,
  }
}
